import React, { useEffect } from "react";
import ReusableModal from "../../../../components/Modals/ReusableModal";
import {
  ACTIVITY_STATUS,
  ACTIVITY_TYPE,
  specialitiesList,
  STATES,
} from "../../../../constants/config.constants";
import { useCreateProviderMutation } from "../../../../features/provider/providerApiSlice";
import { useCreateLogsMutation } from "../../../../features/logs/logsApiSlice";
import Loading from "../../../../components/Loading";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../features/auth/authSlice";
import {
  useFetchProviderTypesMutation,
  useFetchProviderSpecialitiesMutation,
} from "../../../../features/athena/athenaApiSlice";
import Select from "react-select";

const ProviderAddModal = ({ open, setOpen, fetchProviders }) => {
  const user = useSelector(selectUser);
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [speciality, setSpeciality] = React.useState(specialitiesList[1]);
  const [street, setStreet] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [zip, setZip] = React.useState("");
  const [npiId, setNpiId] = React.useState("");
  const [providerEhrId, setProviderEhrId] = React.useState("");
  const [ehrId, setEhrId] = React.useState(1);
  const [errorMsg, setErrorMsg] = React.useState(null);
  const [type, setType] = React.useState("Provider");
  const [entityTypeId, setEntityTypeId] = React.useState("");
  const [medicalGroupId, setMedicalGroupId] = React.useState("");
  const [schedulingName, setSchedulingName] = React.useState("");
  const [providerGroupId, setProviderGroupId] = React.useState("");
  const [providerTypes, setProviderTypes] = React.useState([]);
  const [providerSpecialities, setProviderSpecialities] = React.useState([]);

  // Validation states
  const [errors, setErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const validateForm = (isAthena = false) => {
    const newErrors = {};

    // Required field validations
    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (lastName.trim().length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters";
    }

    if (!entityTypeId.trim()) {
      newErrors.entityTypeId = "Entity type ID is required";
    }

    if (!medicalGroupId.trim()) {
      newErrors.medicalGroupId = "Medical group ID is required";
    }

    if (!schedulingName.trim()) {
      newErrors.schedulingName = "Scheduling name is required";
    }

    if (!providerGroupId.trim()) {
      newErrors.providerGroupId = "Provider group ID is required";
    }

    // Optional field validations
    if (npiId.trim() && !/^\d{10}$/.test(npiId.replace(/\D/g, ""))) {
      newErrors.npiId = "NPI ID must be 10 digits";
    }

    if (zip.trim() && !/^\d{5}(-\d{4})?$/.test(zip)) {
      newErrors.zip = "Please enter a valid ZIP code";
    }

    if (!isAthena && !providerEhrId.trim()) {
      newErrors.providerEhrId = "Provider EHR ID is required";
    }

    // Address validations
    if (city.trim() && !/^[a-zA-Z\s]+$/.test(city)) {
      newErrors.city = "City name should contain only letters";
    }

    if (street.trim() && street.trim().length < 5) {
      newErrors.street = "Street address is too short";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: null }));
    }
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setSpeciality(specialitiesList[1]);
    setStreet("");
    setCity("");
    setState("");
    setZip("");
    setNpiId("");
    setProviderEhrId("");
    setEhrId(1);
    setErrorMsg(null);
    setType("");
    setEntityTypeId("");
    setMedicalGroupId("");
    setSchedulingName("");
    setProviderGroupId("");
    setErrors({});
    setIsSubmitting(false);
  };

  const [createProvider, { isLoading: createLoading }] =
    useCreateProviderMutation();
  const [createLog] = useCreateLogsMutation();
  const [fetchProviderTypes, { isLoading: fetchProviderTypesLoading }] =
    useFetchProviderTypesMutation();
  const [
    fetchProviderSpecialities,
    { isLoading: fetchProviderSpecialitiesLoading },
  ] = useFetchProviderSpecialitiesMutation();

  const isLoading =
    createLoading ||
    fetchProviderTypesLoading ||
    fetchProviderSpecialitiesLoading;

  useEffect(() => {
    Promise.all([
      fetchProviderTypes({
        practiceId: user?.organisation?.practiceId,
      }).unwrap(),
      fetchProviderSpecialities({
        practiceId: user?.organisation?.practiceId,
      }).unwrap(),
    ]).then(([providerTypes, providerSpecialities]) => {
      setProviderTypes(
        providerTypes?.data?.map((type) => ({
          value: type.providertypeid,
          label: type.providertype,
        })) ?? []
      );
      setProviderSpecialities(
        providerSpecialities?.data?.map((speciality) => ({
          value: speciality.specialtyname,
          label: speciality.specialtyname,
        })) ?? []
      );
    });
  }, []);

  const saveProvider = async (isAthena = false) => {
    const payload = {
      activity: `New provider ${firstName} ${lastName} has been created by User - ${user?.details?.name}`,
      type: ACTIVITY_TYPE.PROVIDER_ADD,
    };

    try {
      // Clear previous errors
      setErrors({});
      setErrorMsg(null);

      // Validate form before proceeding
      if (!validateForm(isAthena)) {
        return;
      }

      setIsSubmitting(true);

      await createProvider({
        providerEhrId: providerEhrId || undefined,
        providerNpiId: npiId || undefined,
        providerFirstname: firstName,
        providerLastname: lastName,
        providerSpeciality: speciality,
        providerStreetAddress: street,
        providerCity: city,
        providerState: state,
        providerZip: zip,
        providerType: type,
        providerEntityTypeId: entityTypeId,
        providerMedicalGroupId: medicalGroupId,
        providerSchedulingName: schedulingName,
        providerGroupId: providerGroupId,
        ehrId,
        isPublish: isAthena,
        practiceId: user?.organisation?.practiceId,
      }).unwrap();

      setOpen(false);
      resetForm();
      await fetchProviders({
        limit: 20,
        skip: 0,
        search: "",
        status: ["ACTIVE"],
        zones: [],
        specialities: [],
      });
      await createLog({
        ...payload,
        status: ACTIVITY_STATUS.SUCCESS,
      }).unwrap();
    } catch (error) {
      await createLog({
        ...payload,
        status: ACTIVITY_STATUS.SUCCESS,
      }).unwrap();
      const errorMessage =
        error?.data?.message || "Failed to create provider. Please try again.";
      setErrorMsg(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ReusableModal
      open={open}
      title={"Add new provider"}
      onClose={() => {
        setOpen(false);
        resetForm();
      }}
    >
      {isLoading && <Loading loading={isLoading} />}
      <div className="flex flex-col items-center min-w-[480px]">
        <div className="h-[1px] w-full bg-primarygrayborder" />
        <div className="flex flex-col gap-4 p-6 overflow-auto max-h-[calc(100vh-200px)]">
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="email" className="m-0 text-base font-semibold">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                placeholder="John"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.firstName ? "border-red-500" : "border-primarygray"
                }`}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  clearFieldError("firstName");
                }}
              />
              {errors.firstName && (
                <div className="text-red-500 text-sm">{errors.firstName}</div>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="phone" className="m-0 text-base font-semibold">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                placeholder="Doe"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.lastName ? "border-red-500" : "border-primarygray"
                }`}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  clearFieldError("lastName");
                }}
              />
              {errors.lastName && (
                <div className="text-red-500 text-sm">{errors.lastName}</div>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="type" className="m-0 text-base font-semibold">
                Type
              </label>
              <Select
                name="type"
                id="type"
                className="w-full"
                options={providerTypes}
                onChange={(e) => setType(e.value)}
                value={providerTypes.find((t) => t.value === type)}
                placeholder="Select Type"
                isSearchable
              />
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label
                htmlFor="speciality"
                className="m-0 text-base font-semibold"
              >
                Speciality
              </label>
              <Select
                name="speciality"
                id="speciality"
                className="w-full"
                options={providerSpecialities}
                onChange={(e) => setSpeciality(e.value)}
                value={providerSpecialities.find((s) => s.value === speciality)}
                placeholder="Select Speciality"
                isSearchable
              />
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label
                for="medicalGroupId"
                className="m-0 text-base font-semibold"
              >
                Medical Group Id <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="medicalGroupId"
                name="medicalGroupId"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.medicalGroupId
                    ? "border-red-500"
                    : "border-primarygray"
                }`}
                placeholder="Enter Medical Group Id"
                value={medicalGroupId}
                onChange={(e) => {
                  setMedicalGroupId(e.target.value);
                  clearFieldError("medicalGroupId");
                }}
              />
              {errors.medicalGroupId && (
                <div className="text-red-500 text-sm">
                  {errors.medicalGroupId}
                </div>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="npiId" className="m-0 text-base font-semibold">
                Scheduling Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="schedulingName"
                name="schedulingName"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.schedulingName
                    ? "border-red-500"
                    : "border-primarygray"
                }`}
                placeholder="Enter Scheduling Name"
                value={schedulingName}
                onChange={(e) => {
                  setSchedulingName(e.target.value);
                  clearFieldError("schedulingName");
                }}
              />
              {errors.schedulingName && (
                <div className="text-red-500 text-sm">
                  {errors.schedulingName}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="npiId" className="m-0 text-base font-semibold">
                Entity type id <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="entityTypeId"
                name="entityTypeId"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.entityTypeId ? "border-red-500" : "border-primarygray"
                }`}
                placeholder="Enter Entity Type Id"
                value={entityTypeId}
                onChange={(e) => {
                  setEntityTypeId(e.target.value);
                  clearFieldError("entityTypeId");
                }}
              />
              {errors.entityTypeId && (
                <div className="text-red-500 text-sm">
                  {errors.entityTypeId}
                </div>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="npiId" className="m-0 text-base font-semibold">
                Npi Id
              </label>
              <input
                type="text"
                id="npiId"
                name="npiId"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.npiId ? "border-red-500" : "border-primarygray"
                }`}
                placeholder="Enter Npi Id"
                value={npiId}
                onChange={(e) => {
                  setNpiId(e.target.value);
                  clearFieldError("npiId");
                }}
              />
              {errors.npiId && (
                <div className="text-red-500 text-sm">{errors.npiId}</div>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="ehrId" className="m-0 text-base font-semibold">
                Provider Group Id <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="providerGroupId"
                name="providerGroupId"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.providerGroupId
                    ? "border-red-500"
                    : "border-primarygray"
                }`}
                placeholder="Enter Provider Group Id"
                value={providerGroupId}
                onChange={(e) => {
                  setProviderGroupId(e.target.value);
                  clearFieldError("providerGroupId");
                }}
              />
              {errors.providerGroupId && (
                <div className="text-red-500 text-sm">
                  {errors.providerGroupId}
                </div>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label
                for="providerEhrId"
                className="m-0 text-base font-semibold"
              >
                Provider Ehr Id
              </label>
              <input
                type="text"
                id="providerEhrId"
                name="providerEhrId"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.providerEhrId ? "border-red-500" : "border-primarygray"
                }`}
                placeholder="Enter Provider Ehr Id"
                value={providerEhrId}
                onChange={(e) => {
                  setProviderEhrId(e.target.value);
                  clearFieldError("providerEhrId");
                }}
              />
              {errors.providerEhrId && (
                <div className="text-red-500 text-sm">
                  {errors.providerEhrId}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="street" className="m-0 text-base font-semibold">
                Street
              </label>
              <input
                type="text"
                id="street"
                name="street"
                placeholder="Enter Street"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.street ? "border-red-500" : "border-primarygray"
                }`}
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                  clearFieldError("street");
                }}
              />
              {errors.street && (
                <div className="text-red-500 text-sm">{errors.street}</div>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="city" className="m-0 text-base font-semibold">
                City
              </label>
              <input
                type="text"
                id="city"
                name="city"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.city ? "border-red-500" : "border-primarygray"
                }`}
                placeholder="Enter City"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  clearFieldError("city");
                }}
              />
              {errors.city && (
                <div className="text-red-500 text-sm">{errors.city}</div>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="state" className="m-0 text-base font-semibold">
                State
              </label>
              <select
                id="state"
                name="state"
                placeholder="Select State"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.state ? "border-red-500" : "border-primarygray"
                }`}
                value={state?.trim() || ""}
                onChange={(e) => {
                  setState(e.target.value);
                  clearFieldError("state");
                }}
              >
                <option value="" disabled hidden>
                  Select State
                </option>
                {STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              {errors.state && (
                <div className="text-red-500 text-sm">{errors.state}</div>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="zip" className="m-0 text-base font-semibold">
                Zip
              </label>
              <input
                type="text"
                id="zip"
                name="zip"
                className={`w-full p-2 border-[1px] rounded-md outline-none ${
                  errors.zip ? "border-red-500" : "border-primarygray"
                }`}
                placeholder="Zip code"
                value={zip}
                onChange={(e) => {
                  setZip(e.target.value);
                  clearFieldError("zip");
                }}
              />
              {errors.zip && (
                <div className="text-red-500 text-sm">{errors.zip}</div>
              )}
            </div>
          </div>
        </div>
        {errorMsg && <div className="text-red-500">{errorMsg}</div>}
        <div className="justify-center w-full mb-2 d-flex gap-4 px-3">
          {user?.permissions?.includes('providers_dm.publish_provider_to_athena') && <button
            disabled={
              isSubmitting ||
              createLoading ||
              firstName === "" ||
              lastName === "" ||
              entityTypeId === "" ||
              medicalGroupId === "" ||
              schedulingName === "" ||
              providerGroupId === ""
            }
            className={`w-75 font-semibold p-2 border-[2px] rounded-md text-white whitespace-nowrap ${
              isSubmitting || createLoading
                ? "bg-gray-400 cursor-not-allowed opacity-40"
                : "bg-primarygreen hover:bg-green-600"
            }`}
            onClick={async () => {
              try {
                await saveProvider(true); // isAthena = true
              } catch (error) {
                console.log(error);
                setErrorMsg(error.message);
              }
            }}
          >
            {isSubmitting || createLoading
              ? "Adding to Athena..."
              : "Add to Athena"}
          </button>}
          <button
            disabled={
              isSubmitting ||
              createLoading ||
              firstName === "" ||
              lastName === "" ||
              providerEhrId === "" ||
              entityTypeId === "" ||
              medicalGroupId === "" ||
              schedulingName === "" ||
              providerGroupId === ""
            }
            className={`w-75 font-semibold p-2 border-[2px] rounded-md text-white whitespace-nowrap ${
              isSubmitting || createLoading
                ? "bg-gray-400 cursor-not-allowed opacity-40"
                : "bg-primaryblue hover:bg-blue-600"
            }`}
            onClick={async () => {
              try {
                await saveProvider(false); // isAthena = false
              } catch (error) {
                console.log(error);
                setErrorMsg(error.message);
              }
            }}
          >
            {isSubmitting || createLoading ? "Adding..." : "Add"}
          </button>
        </div>
      </div>
    </ReusableModal>
  );
};

export default ProviderAddModal;
